import { useEffect, useRef, useState } from "react"
import { config } from "~/config.js"

const SINGLE_SCHEMATIC = "/plane-single-schematic.png"
const DOUBLE_SCHEMATIC = "/plane-double-schematic.png"
const VEHICLE_SCHEMATIC = "/vehicle-schematic.png"

export const getDefaultDamageSchematicUrl = (material) => {
  if (material?.category === "kist") {
    return material.kist_pilots > 1 ? DOUBLE_SCHEMATIC : SINGLE_SCHEMATIC
  }
  if (material?.category === "rollend") return VEHICLE_SCHEMATIC
  return null
}

export const getDamageSchematicUrl = (material) =>
  material?.damage_schematic
    ? `${config.media_prefix}${material.damage_schematic}`
    : getDefaultDamageSchematicUrl(material)

// Without an image the schadekaart is disabled for this material
export const hasDamageSchematic = (material) => !!getDamageSchematicUrl(material)

// Area the schematic is drawn in on the canvas (contain). Positions (canvas_x/y)
// are relative to this area, so they don't depend on the canvas size.
export const getImageRect = (background, width, height) => {
  if (!background) return { x: 0, y: 0, width, height }
  const ratio = Math.min(width / background.width, height / background.height)
  const drawWidth = background.width * ratio
  const drawHeight = background.height * ratio
  return { x: (width - drawWidth) / 2, y: (height - drawHeight) / 2, width: drawWidth, height: drawHeight }
}

export const drawSchematic = (ctx, background, rect) => {
  if (background) ctx.drawImage(background, rect.x, rect.y, rect.width, rect.height)
}

export const toCanvasPoint = (rect, position) => ({
  x: rect.x + position.x * rect.width,
  y: rect.y + position.y * rect.height,
})

// null when the point is outside the schematic
export const toImagePosition = (rect, point) => {
  const x = (point.x - rect.x) / rect.width
  const y = (point.y - rect.y) / rect.height
  return x < 0 || x > 1 || y < 0 || y > 1 ? null : { x, y }
}

// mouse position in canvas pixels, also while antd's Modal zoom animation scales the canvas
export const canvasPoint = (event, canvas) => {
  const rect = canvas.getBoundingClientRect()
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.width,
    y: ((event.clientY - rect.top) / rect.height) * canvas.height,
  }
}

// Falls back to the next candidate when an image fails to load
export const useDamageSchematic = (material) => {
  const backgroundRef = useRef(null)
  const [backgroundLoaded, setBackgroundLoaded] = useState(false)
  const url = getDamageSchematicUrl(material)

  useEffect(() => {
    const candidates = [...new Set([
      url,
      getDefaultDamageSchematicUrl(material),
      material?.category === "kist" && SINGLE_SCHEMATIC,
    ].filter(Boolean))]
    const img = new Image()
    let index = 0

    backgroundRef.current = null
    setBackgroundLoaded(!candidates.length)
    if (!candidates.length) return

    img.onload = () => {
      backgroundRef.current = img
      setBackgroundLoaded(true)
    }
    img.onerror = () => {
      index += 1
      if (index < candidates.length) {
        img.src = candidates[index]
      } else {
        setBackgroundLoaded(true)
      }
    }
    img.src = candidates[0]

    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [url])

  return { backgroundRef, backgroundLoaded }
}
